/**
 * Générateur — Diviser par un nombre décimal.
 * Attendu ID : 693 | Niveau : 5ème | Domaine : Opérations (cycle 4 rénové, cluster 2)
 *
 * Cadrage : ramener une division à diviseur décimal à une division à diviseur
 * entier (1,5 ÷ 0,3 = 15 ÷ 3 = 5).
 *
 * Plan de cartes (validé par le responsable pédagogique, arbitrages inclus) :
 *
 *   Carte « transformer » — Ex.1 Transformer en fraction
 *     Formats : texte_trou_saisie, texte_trou_drag. Rendu KaTeX \dfrac avec la
 *     convention TRIPLE ACCOLADE {{{X}}} pour les trous dans un groupe KaTeX
 *     (arbitrage utilisateur). Décimales en KaTeX via {,} (pas d'espace
 *     parasite). k = max(décimales) ∈ {1,2,3} en rotation (progressivité
 *     ×10 → ×100 → ×1 000 du doc). Deux modes : dénominateur donné (1 trou) /
 *     entièrement à compléter (2 trous). Tout en entiers par construction.
 *     Misconceptions (drag) : décalages DIFFÉRENTS sur num. et dén., décalage
 *     d'un cran en trop.
 *
 *   Carte « quotients_egaux » — Ex.4 Quotients égaux
 *     Formats : qcm_multiple, tableau_vf, classer. 6 écritures du même couple
 *     de chiffres ; égal ⟺ le MÊME décalage est appliqué aux deux termes.
 *     Nombre de bonnes réponses VARIABLE (2 à 4), annoncé dans la consigne
 *     (« Sélectionne les trois quotients… ») — arbitrage utilisateur + §2.
 *     Vérités décidées par la construction (a, b), jamais par flottant.
 *
 *   Carte « multiplier_moins » — Ex.6 Multiplier le moins possible
 *     Formats : qcm_unique (question a), texte_trou_saisie guidé (b/c).
 *     D = m/k irréductible (gcd(m,k)=1) avec k ∈ {2, 4, 5, 10} — le cas ×10
 *     est INCLUS (arbitrage utilisateur : éviter le pattern « jamais 10 » ;
 *     diviseurs 0,3 / 0,7 / 0,9, l'exemple du cadrage). k minimal PROUVÉ par
 *     l'irréductibilité. Q à 2 décimales (pair si k=4 pour l'exactitude de N).
 *     Misconceptions : 10 réflexe quand k ≠ 10, multiplicateur non minimal.
 *
 *   Carte « bouteilles » — Ex.10 Le cocktail
 *     Formats : texte_trou_saisie (« 9 ÷ 0,75 = {{900}} ÷ {{75}} = {{12}}
 *     bouteilles »), qcm_unique. V = nb × c posé par construction (division
 *     exacte, nb entier). Décalage adapté aux décimales de c (×10 ou ×100).
 *     Contextes en rotation. Misconceptions : multiplier au lieu de diviser,
 *     mauvais décalage.
 *
 *   Carte « rectangles » (hors quota) — Ex.14 Les deux rectangles
 *     Formats : texte (saisie), qcm_unique. Figure SVG à l'échelle (deux
 *     rectangles, marques d'angles droits, dimensions étiquetées — reproduit
 *     l'exercice source). EF = AB × coef exact par construction (AB pair en
 *     dixièmes × coefficients demi-entiers). Les dimensions figurent AUSSI
 *     dans le texte (accessibilité + solveur).
 *     Misconceptions : EF − AB (soustrait), coef ± 0,5, EF ÷ AD.
 *
 * Adaptations d'énoncés : les batteries a–f du doc deviennent UN item par
 * tirage (Duolingo) avec rotation des difficultés ; Ex.14 : figure support,
 * zone de réponse fermée.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeClasser,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 693;

const L_TRANSF = 'Transformer une écriture fractionnaire en fraction';
const L_EGAUX  = 'Reconnaître des quotients égaux';
const L_MOINS  = 'Multiplier le moins possible pour diviser';
const L_BOUT   = 'Répartir en bouteilles (division en contexte)';
const L_RECT   = 'Trouver le coefficient d\'agrandissement (figure)';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Décale la virgule d'un nombre FR (chaînes, exact). n > 0 → ×10ⁿ. */
const decaler = (valFr, n) => {
    const [ip, dp = ''] = String(valFr).split(',');
    let digits = ip + dp;
    let pos = ip.length + n;
    while (pos > digits.length) digits += '0';
    while (pos < 0) { digits = `0${digits}`; pos++; }
    const ipNew = digits.slice(0, pos).replace(/^0+(?=\d)/, '') || '0';
    const dpNew = digits.slice(pos).replace(/0+$/, '');
    return dpNew ? `${ipNew},${dpNew}` : ipNew;
};

/** Nombre FR → nombre de décimales. */
const nbDec = (valFr) => (String(valFr).split(',')[1] || '').length;

/** Virgule française → virgule KaTeX sans espace parasite : "2,5" → "2{,}5". */
const katexFr = (valFr) => String(valFr).replace(',', '{,}');

/** Dixièmes → écriture FR. */
const dixToFr = (d) => (d % 10 === 0 ? String(d / 10) : `${Math.floor(d / 10)},${d % 10}`);

/** Centièmes → écriture FR sans zéros finaux. */
const centToFr = (c) => {
    const ent = Math.floor(c / 100);
    const cents = c % 100;
    if (cents === 0) return String(ent);
    return `${ent},${String(cents).padStart(2, '0').replace(/0+$/, '')}`;
};

/** Millièmes → écriture FR sans zéros finaux. */
const milToFr = (m) => {
    const ent = Math.floor(m / 1000);
    const mil = m % 1000;
    if (mil === 0) return String(ent);
    return `${ent},${String(mil).padStart(3, '0').replace(/0+$/, '')}`;
};

/** Déduplique des choix de QCM (rotation des misconceptions par mélange). */
const dedupeChoix = (bonne, candidats, complements) => {
    const norm = (s) => String(s).replace(',', '.').trim();
    const vus = new Set([norm(bonne)]);
    const retenus = [];
    [...shuffle(candidats), ...complements].forEach(c => {
        const n = norm(c);
        if (!vus.has(n) && retenus.length < 3) { vus.add(n); retenus.push(String(c)); }
    });
    return retenus;
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Transformer une écriture fractionnaire en fraction
// ═══════════════════════════════════════════════════════════

/**
 * Tire un couple (numérateur, dénominateur) décimal dont AU MOINS UN a
 * exactement k décimales (k = 1, 2 ou 3 en rotation — progressivité du doc).
 * Représentation : entier de chiffres + nombre de décimales → exactitude.
 */
const tirerDecimal = (maxDec) => {
    const dec = randInt(0, maxDec);
    let digits;
    if (dec === 0) digits = randInt(2, 99);
    else {
        // Dernier chiffre non nul → nbDec exact, pas de zéro final trompeur
        digits = randInt(Math.pow(10, dec), Math.pow(10, dec + 1) * 9 - 1);
        if (digits % 10 === 0) digits += randomChoice([1, 3, 7, 9]);
    }
    const valFr = dec === 0 ? String(digits) : decaler(String(digits), -dec);
    return { valFr, dec: nbDec(valFr) };
};

const tirerTransformer = () => {
    const k = randomChoice([1, 2, 3]);
    // Au moins un des deux termes a exactement k décimales
    let num, den;
    for (let i = 0; i < 30; i++) {
        num = tirerDecimal(k);
        den = tirerDecimal(k);
        if (Math.max(num.dec, den.dec) === k && den.valFr !== '0' && num.valFr !== den.valFr) break;
    }
    if (Math.max(num.dec, den.dec) !== k) {
        num = { valFr: decaler(String(randInt(11, 99)), -k), dec: k };
        den = { valFr: String(randInt(3, 49)), dec: 0 };
    }
    return {
        k,
        num: num.valFr,
        den: den.valFr,
        numEntier: decaler(num.valFr, k),   // entiers par décalage de chaîne
        denEntier: decaler(den.valFr, k),
    };
};

/** Phrase KaTeX : \dfrac{2{,}5}{3{,}8} = \dfrac{{{25}}}{38} (triple accolade). */
const phraseTransformer = (t, modeComplet) => {
    const denPart = modeComplet ? `{{{${t.denEntier}}}}` : `{${t.denEntier}}`;
    return `$\\dfrac{${katexFr(t.num)}}{${katexFr(t.den)}} = \\dfrac{{{${t.numEntier}}}}${denPart}$`;
};

const genTransformerTrouSaisie = () => {
    const t = tirerTransformer();
    const modeComplet = randomChoice([true, false]);
    const texte = '<p>Transforme cette écriture fractionnaire en fraction de nombres entiers. ' +
        'Complète :</p>';
    return makeTexteTrou(phraseTransformer(t, modeComplet), texte, { ...opts(L_TRANSF), mode: 'saisie' });
};

const genTransformerTrouDrag = () => {
    const t = tirerTransformer();
    const phrase = phraseTransformer(t, true);
    // Misconceptions : décalage d'un cran en trop / en moins / deux crans en
    // trop sur chaque terme. 6 candidats → ≥ 3 entiers distincts survivent
    // toujours (les « k−1 » peuvent rester décimaux → filtrés).
    const candidats = shuffle([
        decaler(t.num, t.k + 1), decaler(t.num, t.k - 1), decaler(t.num, t.k + 2),
        decaler(t.den, t.k + 1), decaler(t.den, t.k - 1), decaler(t.den, t.k + 2),
    ]);
    // Filtre local : ni égal aux mots attendus, ni décimal résiduel ambigu
    const attendus = new Set([t.numEntier, t.denEntier]);
    const distracteurs = [...new Set(candidats.filter(c => !attendus.has(c) && !c.includes(',')))].slice(0, 3);
    const texte = '<p>Transforme cette écriture fractionnaire en fraction de nombres entiers. ' +
        'Glisse les bonnes étiquettes :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_TRANSF), mode: 'drag', distracteurs });
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Quotients égaux
// ═══════════════════════════════════════════════════════════

const MOTS_NOMBRE = { 2: 'deux', 3: 'trois', 4: 'quatre' };

/**
 * Base N ÷ D (chiffres sans zéro final). Candidats = décalages (a, b) :
 * égal ⟺ a = b. Écritures toutes distinctes par construction (mêmes chiffres,
 * virgules à des positions différentes).
 */
const tirerQuotientsEgaux = () => {
    const nDigits = randInt(111, 989);
    const dDigits = randInt(11, 98);
    const N = decaler(String(nDigits % 10 === 0 ? nDigits + 3 : nDigits), -1);   // ex. 56,7
    const D = decaler(String(dDigits % 10 === 0 ? dDigits + 7 : dDigits), -1);   // ex. 2,8
    const nbEgaux = randInt(2, 4);
    const shiftsEgaux = shuffle([-2, -1, 1, 2, 3]).slice(0, nbEgaux);
    const desequilibres = shuffle([[-1, 1], [1, -1], [0, 1], [1, 0], [-1, 0], [0, -1], [2, 1], [1, 2]])
        .slice(0, 6 - nbEgaux);
    const candidats = shuffle([
        ...shiftsEgaux.map(s => ({ txt: `${decaler(N, s)} ÷ ${decaler(D, s)}`, egal: true })),
        ...desequilibres.map(([a, b]) => ({ txt: `${decaler(N, a)} ÷ ${decaler(D, b)}`, egal: false })),
    ]);
    return { N, D, nbEgaux, candidats };
};

const genQuotientsEgauxQcmMulti = () => {
    const { N, D, nbEgaux, candidats } = tirerQuotientsEgaux();
    const corrects = candidats.filter(c => c.egal).map(c => c.txt);
    const texte = `<p>Sélectionne les <strong>${MOTS_NOMBRE[nbEgaux]}</strong> quotients égaux à ` +
        `<strong>${N} ÷ ${D}</strong>.</p>`;
    return makeQcm(candidats.map(c => c.txt), corrects, texte, opts(L_EGAUX), true);
};

const genQuotientsEgauxVF = () => {
    const { N, D, candidats } = tirerQuotientsEgaux();
    // 4 lignes : on garantit au moins un vrai ET un faux par tirage
    const egaux = candidats.filter(c => c.egal).slice(0, 2);
    const differents = candidats.filter(c => !c.egal).slice(0, 2);
    const lignes4 = shuffle([...egaux, ...differents]);
    const texte = `<p>Pour chaque quotient, indique s'il est égal à <strong>${N} ÷ ${D}</strong>.</p>`;
    return makeVraiFaux(
        lignes4.map(c => `${c.txt} est égal à ${N} ÷ ${D}`),
        lignes4.map(c => (c.egal ? 0 : 1)),
        texte, opts(L_EGAUX),
    );
};

const genQuotientsEgauxClasser = () => {
    const { N, D, candidats } = tirerQuotientsEgaux();
    const categories = [
        { name: `Égal à ${N} ÷ ${D}`, items: candidats.filter(c => c.egal).map(c => c.txt) },
        { name: `Différent de ${N} ÷ ${D}`, items: candidats.filter(c => !c.egal).map(c => c.txt) },
    ];
    const texte = `<p>Classe chaque quotient : est-il égal à <strong>${N} ÷ ${D}</strong> ou non ?</p>`;
    return makeClasser(categories, texte, opts(L_EGAUX));
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Multiplier le moins possible
// ═══════════════════════════════════════════════════════════

/**
 * D = m/k irréductible → k est LE plus petit multiplicateur rendant D entier.
 * k ∈ {2, 4, 5, 10} (le cas 10 est inclus — diviseurs 0,3 / 0,7 / 0,9).
 * Q en centièmes (pair si k = 4) → N = D×Q exact en millièmes.
 */
const CONFIGS_MOINS = [
    { k: 2,  ms: [3, 5, 7, 9, 11, 13] },              // D = 1,5 … 6,5
    { k: 4,  ms: [3, 5, 7, 9, 11, 13] },              // D = 0,75 … 3,25
    { k: 5,  ms: [2, 3, 4, 6, 7, 8, 9, 11, 12, 13] }, // D = 0,4 … 2,6
    { k: 10, ms: [3, 7, 9, 11, 13, 17, 19, 21] },     // D = 0,3 … 2,1 (cadrage)
];

const tirerMoins = () => {
    const cfg = randomChoice(CONFIGS_MOINS);
    const m = randomChoice(cfg.ms);
    const k = cfg.k;
    let qC = randInt(150, 999);                     // quotient en centièmes
    if (qC % 10 === 0) qC += randomChoice([1, 3, 7]);
    if (k === 4 && qC % 2 !== 0) qC += 1;           // exactitude de N (millièmes)
    const dFr = k === 10 ? decaler(String(m), -1)
        : k === 4 ? centToFr(m * 25)
            : k === 5 ? dixToFr(m * 2)
                : dixToFr(m * 5);                   // k=2 → m×0,5
    const nMil = m * qC * 10 / k;                   // N en millièmes (entier par construction)
    return {
        k, m, dFr,
        nFr: milToFr(nMil),
        nPrimeFr: centToFr(m * qC),                 // N × k
        qFr: centToFr(qC),
    };
};

const genMoinsQcm = () => {
    const t = tirerMoins();
    const bonne = String(t.k);
    const candidats = t.k === 10 ? ['2', '5', '100'] : ['10', String(t.k * 2), '100'];
    const distracteurs = dedupeChoix(bonne, candidats, ['20', '1000']);
    const texte = `<p>Quel est le plus petit nombre entier différent de 0 par lequel on doit ` +
        `multiplier <strong>${t.dFr}</strong> pour obtenir un résultat entier ?</p>`;
    return makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_MOINS));
};

const genMoinsTrouSaisie = () => {
    const t = tirerMoins();
    // Style feuille : on réécrit le quotient à chaque ligne
    const phrase =
        `${t.nFr} ÷ ${t.dFr} = (${t.nFr} × {{${t.k}}}) ÷ (${t.dFr} × {{${t.k}}})\n` +
        `${t.nFr} ÷ ${t.dFr} = {{${t.nPrimeFr}}} ÷ {{${t.m}}}\n` +
        `${t.nFr} ÷ ${t.dFr} = {{${t.qFr}}}`;
    const texte = '<p>Calcule ce quotient <strong>en multipliant le moins possible</strong>. ' +
        'Complète (écris les nombres sans zéro inutile, ex. : 12,5) :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_MOINS), mode: 'saisie' });
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Répartir en bouteilles
// ═══════════════════════════════════════════════════════════

const CTX_BOUTEILLES = [
    { qui: 'Chiara', liquide: 'de cocktail', recipient: 'bouteilles', recipientSing: 'bouteille' },
    { qui: 'Malik', liquide: 'de sirop de menthe', recipient: 'flacons', recipientSing: 'flacon' },
    { qui: 'Nora', liquide: "d'huile d'olive", recipient: 'bidons', recipientSing: 'bidon' },
    { qui: 'Élio', liquide: 'de limonade', recipient: 'carafes', recipientSing: 'carafe' },
];

const CONTENANCES = [25, 50, 75, 150];   // centièmes de litre

const tirerBouteilles = () => {
    const cC = randomChoice(CONTENANCES);
    const nb = randInt(8, 24);
    const vC = nb * cC;                                // volume total en centièmes
    const j = cC % 10 === 0 ? 1 : 2;                   // décalage : ×10 ou ×100
    return {
        nb, ctx: randomChoice(CTX_BOUTEILLES),
        vFr: centToFr(vC), cFr: centToFr(cC),
        vDecale: decaler(centToFr(vC), j),
        cDecale: decaler(centToFr(cC), j),
    };
};

const enonceBouteilles = ({ vFr, cFr, ctx }) =>
    `<p>${ctx.qui} a préparé <strong>${vFr} L</strong> ${ctx.liquide} et veut tout répartir ` +
    `dans des ${ctx.recipient} de <strong>${cFr} L</strong>. ` +
    `De combien de ${ctx.recipient} a-t-${['Chiara', 'Nora'].includes(ctx.qui) ? 'elle' : 'il'} besoin ?</p>`;

const genBouteillesTrouSaisie = () => {
    const t = tirerBouteilles();
    const phrase =
        `${t.vFr} ÷ ${t.cFr} = {{${t.vDecale}}} ÷ {{${t.cDecale}}}\n` +
        `Nombre de ${t.ctx.recipient} : {{${t.nb}}}`;
    const texte = enonceBouteilles(t) + '<p>Complète :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_BOUT), mode: 'saisie' });
};

const genBouteillesQcm = () => {
    const t = tirerBouteilles();
    const bonne = String(t.nb);
    const distracteurs = dedupeChoix(bonne,
        [String(t.nb * 10), String(t.nb + 1), String(t.nb - 1)],
        [String(t.nb + 2), String(t.nb + 3)]);
    const texte = enonceBouteilles(t);
    return makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_BOUT));
};

// ═══════════════════════════════════════════════════════════
// Carte 5 (hors quota) — Les deux rectangles (figure)
// ═══════════════════════════════════════════════════════════

/** AB pair (dixièmes) × coefficient demi-entier → EF exact en dixièmes. */
const tirerRectangles = () => {
    const ab = randomChoice([24, 26, 28, 32, 34, 36, 38, 42, 44, 46, 48]).valueOf();
    const abPair = ab % 2 === 0 ? ab : ab + 1;
    const ad = randInt(10, 20);
    const coef10 = randomChoice([15, 20, 25, 30, 35]);
    const ef = abPair * coef10 / 10;                  // dixièmes, entier par parité
    return { ab: abPair, ad, coef10, ef };
};

const svgRectangles = ({ ab, ad, ef, coef10 }) => {
    const s = 22;                                     // px par cm
    const w1 = ab * s / 10, h1 = ad * s / 10;
    const w2 = ef * s / 10, h2 = ad * coef10 / 10 * s / 10;
    const pad = 56, gap = 34;
    const W = Math.round(Math.max(w1, w2) + 2 * pad);
    const x1 = (W - w1) / 2, y1 = 30;
    const x2 = (W - w2) / 2, y2 = y1 + h1 + gap;
    const H = Math.round(y2 + h2 + 30);
    const angle = (x, y, dx, dy) =>
        `<path d="M ${x + dx * 9} ${y} L ${x + dx * 9} ${y + dy * 9} L ${x} ${y + dy * 9}" fill="none" stroke="#1E3A8A" stroke-width="1.2"/>`;
    const rect = (x, y, w, h) =>
        `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="none" stroke="#1E3A8A" stroke-width="2"/>` +
        angle(x, y, 1, 1) + angle(x + w, y, -1, 1) + angle(x, y + h, 1, -1) + angle(x + w, y + h, -1, -1);
    const lbl = (x, y, txt, anchor = 'middle', bold = false) =>
        `<text x="${x}" y="${y}" text-anchor="${anchor}" font-size="14"${bold ? ' font-weight="bold"' : ''} fill="#0f172a">${txt}</text>`;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" font-family="sans-serif">` +
        rect(x1, y1, w1, h1) + rect(x2, y2, w2, h2) +
        lbl(x1 - 8, y1 - 6, 'A', 'end', true) + lbl(x1 + w1 + 8, y1 - 6, 'B', 'start', true) +
        lbl(x1 - 8, y1 + h1 + 14, 'D', 'end', true) + lbl(x1 + w1 + 8, y1 + h1 + 14, 'C', 'start', true) +
        lbl(x1 + w1 / 2, y1 - 8, `${dixToFr(ab)} cm`) +
        lbl(x1 - 12, y1 + h1 / 2 + 5, `${dixToFr(ad)} cm`, 'end') +
        lbl(x2 - 8, y2 - 6, 'E', 'end', true) + lbl(x2 + w2 + 8, y2 - 6, 'F', 'start', true) +
        lbl(x2 - 8, y2 + h2 + 14, 'H', 'end', true) + lbl(x2 + w2 + 8, y2 + h2 + 14, 'G', 'start', true) +
        lbl(x2 + w2 / 2, y2 - 8, `${dixToFr(ef)} cm`) +
        '</svg>';
};

const imageElement = (svg) => ({
    version: 1,
    elements: [{
        type: 'image',
        config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'compact' },
    }],
});

const enonceRectangles = ({ ab, ad, ef }) =>
    `<p>Les dimensions du rectangle ABCD (AB = ${dixToFr(ab)} cm, AD = ${dixToFr(ad)} cm) ont été ` +
    `multipliées par un même nombre pour obtenir celles du rectangle EFGH (EF = ${dixToFr(ef)} cm).</p>`;

const genRectanglesSaisie = () => {
    const t = tirerRectangles();
    const texte = enonceRectangles(t) +
        '<p>Calcule ce nombre. Écris ta réponse en écriture décimale (ex. : 2,5).</p>';
    return makeSaisie(dixToFr(t.coef10), texte, {
        ...opts(L_RECT),
        elements_additionnels: imageElement(svgRectangles(t)),
    });
};

const genRectanglesQcm = () => {
    const t = tirerRectangles();
    const bonne = dixToFr(t.coef10);
    // EF − AB (soustrait au lieu de diviser), coef ± 0,5, EF ÷ AD (mauvaise dimension)
    const efSurAd = Math.round(t.ef * 10 / t.ad);     // en dixièmes, arrondi (distracteur)
    const distracteurs = dedupeChoix(bonne,
        [dixToFr(t.ef - t.ab), dixToFr(t.coef10 + 5), dixToFr(efSurAd)],
        [dixToFr(t.coef10 - 5), dixToFr(t.coef10 + 10)]);
    const texte = enonceRectangles(t) + '<p>Calcule ce nombre.</p>';
    const payload = makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_RECT));
    payload.elements_additionnels = imageElement(svgRectangles(t));
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    transformer_texte_trou_saisie:     genTransformerTrouSaisie,
    transformer_texte_trou_drag:       genTransformerTrouDrag,
    quotients_egaux_qcm_multiple:      genQuotientsEgauxQcmMulti,
    quotients_egaux_tableau_vf:        genQuotientsEgauxVF,
    quotients_egaux_classer:           genQuotientsEgauxClasser,
    multiplier_moins_qcm_unique:       genMoinsQcm,
    multiplier_moins_texte_trou_saisie: genMoinsTrouSaisie,
    bouteilles_texte_trou_saisie:      genBouteillesTrouSaisie,
    bouteilles_qcm_unique:             genBouteillesQcm,
    rectangles_texte:                  genRectanglesSaisie,
    rectangles_qcm_unique:             genRectanglesQcm,
};

export const CATEGORIES = [
    {
        id: 'transformer', label: L_TRANSF,
        formatsList: [
            { key: 'transformer_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'transformer_texte_trou_drag',   label: 'Drag' },
        ],
    },
    {
        id: 'quotients_egaux', label: L_EGAUX,
        formatsList: [
            { key: 'quotients_egaux_qcm_multiple', label: 'QCM Multi' },
            { key: 'quotients_egaux_tableau_vf',   label: 'Vrai/Faux' },
            { key: 'quotients_egaux_classer',      label: 'Classer' },
        ],
    },
    {
        id: 'multiplier_moins', label: L_MOINS,
        formatsList: [
            { key: 'multiplier_moins_qcm_unique',        label: 'QCM' },
            { key: 'multiplier_moins_texte_trou_saisie', label: 'Saisie trous' },
        ],
    },
    {
        id: 'bouteilles', label: L_BOUT,
        formatsList: [
            { key: 'bouteilles_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'bouteilles_qcm_unique',        label: 'QCM' },
        ],
    },
    {
        id: 'rectangles', label: L_RECT,
        formatsList: [
            { key: 'rectangles_texte',      label: 'Saisie' },
            { key: 'rectangles_qcm_unique', label: 'QCM' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    decaler, nbDec, katexFr, dixToFr, centToFr, milToFr,
    tirerTransformer, tirerQuotientsEgaux, tirerMoins, tirerBouteilles, tirerRectangles,
    CONFIGS_MOINS, svgRectangles,
};
